import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Alert,
  Card,
  Checkbox,
  Button,
  Input,
  Typography,
  Space,
  Select,
  Row,
  Col,
  Tooltip,
  Tag,
  message,
  Switch,
  Affix,
  Collapse,
} from "antd";
import SyntaxHighlighter from "react-syntax-highlighter/dist/esm/light";
import js from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import html from "react-syntax-highlighter/dist/esm/languages/hljs/xml";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { useGlobal } from "../contexts/GlobalContext";
import { useCurrentProject } from "../hooks/useCurrentProject";
import {
  InfoCircleOutlined,
  CodeOutlined,
  CopyOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import request from "../utils/request";
import { defaultConfigParams, generateCode, type UserMode } from "../lib/codegen";

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

// 注册语言
SyntaxHighlighter.registerLanguage("javascript", js);
SyntaxHighlighter.registerLanguage("html", html);

interface AgentRecord {
  id: number;
  project_id?: number;
  name: string;
  agent_type?: string;
  status?: string;
  share_enable?: boolean;
  share_token?: string;
}

interface AgentListResult {
  items: AgentRecord[];
  total: number;
  page: number;
  size: number;
}

interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

// 定义配置组的类型
type ConfigGroup = {
  title: string;
  items: ConfigItem[];
};

// 修改 ConfigItem 接口定义，添加必要的类型约束
interface ConfigItem {
  key: keyof typeof defaultConfigParams;
  label: string;
  type: "input" | "color" | "checkbox" | "select" | "switch";
  tooltip?: string;
  defaultValue: any;
  options?: Array<{ label: string; value: string }>;
  span?: number;
  placeholder?: string;
  hidden?: (configParams: typeof defaultConfigParams) => boolean;
  mode?: "tags" | "multiple";
}

const CodeGenerator: React.FC = () => {
  const { projects, API_BASE_URL, token } = useGlobal();
  const { project: routeProject } = useCurrentProject();
  const [code, setCode] = useState("");
  const [legacyCode, setLegacyCode] = useState("");
  const [copied, setCopied] = useState(false);

  // 从 localStorage 获取保存的配置，如果没有则使用默认值
  const getSavedConfig = () => {
    if (!projects.length) return defaultConfigParams;
    const projectConfig = localStorage.getItem(
      `tianliGPT_config_${projects[0].name}`
    );
    return projectConfig ? JSON.parse(projectConfig) : defaultConfigParams;
  };

  // 设置状态，使用 localStorage 中的值作为初始值
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<number>();
  const [key, setKey] = useState("");
  const [currentProjectType, setCurrentProjectType] = useState<
    "postchat" | "summary"
  >("summary");
  const [chatAssistant, setChatAssistant] = useState(false);
  const [articleSummary, setArticleSummary] = useState(true);
  const [configParams, setConfigParams] = useState<typeof defaultConfigParams>(
    getSavedConfig()
  );

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId),
    [agents, selectedAgentId]
  );

  // 当配置参数改变时，只保存项目特定的配置
  useEffect(() => {
    if (configParams && selectedProject) {
      localStorage.setItem(
        `tianliGPT_config_${selectedProject}`,
        JSON.stringify(configParams)
      );
    }
  }, [configParams, selectedProject]);

  // 项目作用域：默认值跟随路由项目（无路由项目时回落第一个）
  useEffect(() => {
    const target = routeProject || projects[0];
    if (target) {
      setSelectedProject(target.id);
      setKey(target.key_value);
      setCurrentProjectType(target.is_postchat ? "postchat" : "summary");
      setChatAssistant(target.is_postchat);
      setArticleSummary(true);

      // 从 localStorage 获取该项目的配置
      const projectConfig = localStorage.getItem(
        `tianliGPT_config_${target.id}`
      );
      if (projectConfig) {
        setConfigParams(JSON.parse(projectConfig));
      }
    }
  }, [routeProject, projects]);

  useEffect(() => {
    if (!token || !selectedProject || currentProjectType !== "postchat") {
      setAgents([]);
      setSelectedAgentId(undefined);
      return;
    }

    setAgentsLoading(true);
    request
      .get<ApiResponse<AgentListResult>>(`${API_BASE_URL}/api/v3/agents`, {
        params: {
          page: 1,
          pageSize: 50,
          project_id: selectedProject,
        },
      })
      .then((response) => {
        if (response.data.code !== 200) {
          throw new Error(response.data.msg || "获取智能体列表失败");
        }

        const nextAgents = response.data.data?.items || [];
        setAgents(nextAgents);
        setSelectedAgentId((current) => {
          if (current && nextAgents.some((agent) => agent.id === current)) {
            return current;
          }
          return (
            nextAgents.find((agent) => agent.agent_type === "workflow")?.id ||
            nextAgents[0]?.id
          );
        });
      })
      .catch((error) => {
        console.error("获取智能体列表失败:", error);
        setAgents([]);
        setSelectedAgentId(undefined);
      })
      .finally(() => setAgentsLoading(false));
  }, [API_BASE_URL, currentProjectType, selectedProject, token]);

  // 修改 updateCode 函数
  const updateCode = useCallback(() => {
    const { unified, legacy } = generateCode({
      key,
      articleSummary,
      chatAssistant,
      configParams,
      agentShareToken: selectedAgent?.share_token,
    });
    setCode(unified);
    setLegacyCode(legacy);
  }, [chatAssistant, articleSummary, key, configParams, selectedAgent]);

  useEffect(() => {
    updateCode();
  }, [updateCode]);

  // 如果没有项目，返回提示组件
  if (projects.length === 0) {
    return (
      <div
        style={{
          background: "#f0f2f5",
          minHeight: "100%",
          borderRadius: 8,
          padding: "16px",
        }}
      >
        <Card
          variant="outlined"
          className="shadow-card"
          style={{ borderRadius: 12 }}
        >
          <div style={{ textAlign: "center", padding: "32px" }}>
            <CodeOutlined
              style={{ fontSize: 64, color: "#bfbfbf", marginBottom: 24 }}
            />
            <Title level={3}>请先创建一个项目</Title>
            <Paragraph type="secondary" style={{ marginBottom: 32 }}>
              创建项目后，您将获得专属的Key，可用于接入聊天助手和文章摘要功能
            </Paragraph>
            <Button
              type="primary"
              size="large"
              onClick={() => (window.location.href = "/console")}
              style={{ minWidth: 200 }}
            >
              返回首页创建项目
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // 更新支持的系统数据
  const supportedSystems = [
    {
      name: "Hexo",
      icon: "ri-hexagon-line",
      url: "https://postchat.zhheo.com/plugin/hexo.html",
      color: "#0E83CD",
    },
    {
      name: "Halo",
      icon: "ri-cloud-line",
      url: "https://postchat.zhheo.com/plugin/halo.html",
      color: "#3570E3",
    },
    {
      name: "WordPress",
      icon: "ri-wordpress-line",
      url: "https://postchat.zhheo.com/plugin/wordpress.html",
      color: "#21759B",
    },
    {
      name: "Discuz",
      icon: "ri-discuss-line",
      url: "https://postchat.zhheo.com/plugin/discuz.html",
      color: "#5D7895",
    },
    {
      name: "emlog",
      icon: "ri-article-line",
      url: "https://postchat.zhheo.com/plugin/emlog.html",
      color: "#4DACF1",
    },
    {
      name: "Typecho",
      icon: "ri-terminal-box-line",
      url: "https://postchat.zhheo.com/plugin/typecho.html",
      color: "#6E7173",
    },
    {
      name: "Vitepress",
      icon: "ri-book-2-line",
      url: "https://postchat.zhheo.com/plugin/vitepress.html",
      color: "#42B883",
    },
    {
      name: "Z-Blog",
      icon: "ri-book-2-line",
      url: "https://postchat.zhheo.com/plugin/zblog.html",
      color: "#B71ED7",
    },
  ];

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    message.success("代码已复制到剪贴板");
    setTimeout(() => setCopied(false), 2000);
  };

  // 将 CONFIG_GROUPS 移到组件内部
  const CONFIG_GROUPS: Record<string, ConfigGroup> = {
    chat: {
      title: "聊天助手配置",
      items: [
        {
          key: "userMode",
          label: "聊天模式",
          type: "select",
          tooltip: `用户唤起聊天的方式：

magic: 现代模式，直接执行在网页中
iframe: 兼容模式，支持聊天历史记录，适合重度聊天任务`,
          defaultValue: "magic" as UserMode,
          options: [
            { label: "现代模式", value: "magic" },
            { label: "兼容模式", value: "iframe" },
          ] as Array<{ label: string; value: UserMode }>,
          span: 24,
        },
        {
          key: "addButton",
          label: "显示按钮",
          type: "switch",
          tooltip: `是否显示聊天按钮，true为显示，false为不显示。
如果设置为false，建议将按钮的参数（背景色、位置等）设置得与你自己创建的按钮参数相同，以便更好地计算窗口宽高和位置。`,
          defaultValue: true,
          span: 24,
        },
        {
          key: "backgroundColor",
          label: "背景颜色",
          type: "color",
          tooltip: "聊天按钮的背景颜色",
          defaultValue: "#3e86f6",
          span: 12,
        },
        {
          key: "fill",
          label: "图标颜色",
          type: "color",
          tooltip: "聊天按钮中图标的颜色",
          defaultValue: "#FFFFFF",
          span: 12,
        },
        {
          key: "bottom",
          label: "底部距离",
          type: "input",
          tooltip: "按钮距离页面底部的距离，支持任何CSS长度单位",
          defaultValue: "16px",
          span: 8,
        },
        {
          key: "left",
          label: "左侧距离",
          type: "input",
          tooltip:
            "按钮距离左侧的边距，如果填写负值，则是距离右侧的边距。例如left为-3px，实际为right 3px",
          defaultValue: "16px",
          span: 8,
        },
        {
          key: "width",
          label: "图标宽度",
          type: "input",
          tooltip: "聊天按钮的宽度，支持任何CSS长度单位",
          defaultValue: "44px",
          span: 8,
        },
        {
          key: "frameWidth",
          label: "窗口宽度",
          type: "input",
          tooltip: "聊天窗口的宽度，仅在iframe模式下有效，支持任何CSS长度单位",
          defaultValue: "375px",
          span: 12,
        },
        {
          key: "frameHeight",
          label: "窗口高度",
          type: "input",
          tooltip: "聊天窗口的高度，仅在iframe模式下有效，支持任何CSS长度单位",
          defaultValue: "600px",
          span: 12,
        },
        {
          key: "defaultInput",
          label: "自动填充标题",
          type: "switch",
          tooltip:
            "开启后，用户点击按钮时会自动添加本页面标题，让用户更方便的询问关于本页的内容",
          defaultValue: true,
          span: 24,
        },
        {
          key: "showInviteLink",
          label: "显示邀请链接",
          type: "switch",
          tooltip: `是否用户点击PostChat对话中的图标后会通过邀请链接进入PostChat。

通过这种方式邀请的好友如果开通了会员，可以获得会员时长奖励。

如果不填写此参数，默认为false。`,
          defaultValue: false,
          span: 24,
        },
        {
          key: "upLoadWeb",
          label: "自动上传内容",
          type: "switch",
          tooltip: `是否上传当前页面内容到知识库，true为上传，推荐开启。

开启后，任意用户访问网页都会将网页内容发送到知识库，无需人工干预即可上传。

上传内容为文章所有文字内容，上限2000字，取前1000和后1000字。`,
          defaultValue: true,
          span: 24,
        },
        {
          key: "userTitle",
          label: "聊天界面标题",
          type: "input",
          tooltip: "自定义PostChat聊天界面的标题文字",
          defaultValue: "",
          placeholder: "请输入聊天界面标题",
          span: 24,
        },
        {
          key: "userDesc",
          label: "聊天界面描述",
          type: "input",
          tooltip: "自定义PostChat聊天界面的描述文字",
          defaultValue: "",
          placeholder: "请输入聊天界面描述",
          span: 24,
        },
        {
          key: "userIcon",
          label: "AI头像图标",
          type: "input",
          tooltip: "自定义AI的头像图标URL，仅在Magic模式下有效",
          defaultValue: "",
          placeholder: "请输入图标URL地址",
          span: 24,
          hidden: (configParams) => configParams.userMode !== "magic",
        },
        {
          key: "defaultChatQuestions",
          label: "默认聊天问题",
          type: "select",
          tooltip:
            "设置默认的聊天问题列表，仅在Magic模式下有效。用户可以快速选择这些预设问题进行提问。",
          defaultValue: [],
          placeholder: "请输入问题后按回车添加",
          span: 24,
          mode: "tags",
          hidden: (configParams) => configParams.userMode !== "magic",
        },
        {
          key: "defaultSearchQuestions",
          label: "默认搜索问题",
          type: "select",
          tooltip:
            "设置默认的搜索问题列表，仅在Magic模式下有效。用户可以快速选择这些预设问题进行搜索。",
          defaultValue: [],
          placeholder: "请输入问题后按回车添加",
          span: 24,
          mode: "tags",
          hidden: (configParams) => configParams.userMode !== "magic",
        },
        {
          key: "blackDom",
          label: "屏蔽选择器",
          type: "select",
          tooltip: `设置要屏蔽的容器内容，仅在仅开启聊天助手（未开启文章摘要）时有效。

例如：如果你在网页中插入了aplayer，为了避免提交的内容包含aplayer的歌词信息，你需要将包含aplayer的元素用选择器的方式屏蔽掉。

可以添加多个选择器，如：.aplayer、#content 等`,
          defaultValue: [],
          placeholder: "请输入CSS选择器后按回车添加",
          span: 24,
          mode: "tags",
          hidden: () => !chatAssistant || articleSummary,
        },
        {
          key: "hotWords",
          label: "热词功能",
          type: "switch",
          tooltip: `是否开启热词功能，开启后将自动展示热门关键词，帮助用户快速提问。`,
          defaultValue: true,
          span: 24,
        },
      ],
    },
    article: {
      title: "文章摘要配置",
      items: [
        {
          key: "postSelector",
          label: "文章选择器",
          type: "input",
          tooltip:
            "请填写网站中文章内容的CSS选择器，例如：article.post、div.content 等",
          defaultValue: "",
          placeholder: "请输入CSS选择器，例如：article.post",
        },
        {
          key: "postURL",
          label: "URL匹配规则",
          type: "input",
          tooltip:
            "当主题存在其他页面和文章页面结构相同时，可以通过通配符url域名来实现只在指定域名中执行",
          defaultValue: "*",
        },
        {
          key: "blacklist",
          label: "黑名单配置",
          type: "input",
          tooltip: `当你有一些页面不想要使用文章摘要，可以将所有的黑名单放入一个json文件中。

json文件格式示例：
{
  "blackurls": [
    "https://example.com/somepath/*",
    "https://*.example.com/",
    "https://www.example.org/specificpage.html"
  ]
}`,
          defaultValue: "",
          placeholder: "https://example.com/blacklist.json",
        },
        {
          key: "wordLimit",
          label: "字数限制",
          type: "input",
          tooltip: `⚠️危险：更改此变量损失已消耗过的Token，因为你提交的内容发生了变化。

可以设置提交的字数限制，默认为1000字。这意味着每个文章最多消耗你1000字符（因为只有提交扣费，生成文本不扣费）。

你可以降低字符数来让扣费变得更少，也可以增加字符数让文章变得更准确。上限为5000，超过5000字符将被截断。`,
          defaultValue: "1000",
          placeholder: "请输入1-5000之间的数字",
        },
        {
          key: "typingAnimate",
          label: "打字动画",
          type: "switch",
          tooltip: "控制是否启用打字动画效果，关闭后将直接显示完整文本",
          defaultValue: true,
          span: 24,
        },
        {
          key: "title",
          label: "AI摘要标题",
          type: "input",
          tooltip: '当设置此变量后，TianGPT顶部的名称"AI摘要"可以被修改',
          defaultValue: "宇宙无敌智能摘要",
          placeholder: "请输入自定义标题",
        },
        {
          key: "injectDom",
          label: "插入位置",
          type: "input",
          tooltip: `当设置此变量后，文章摘要将不插入到文章之前，而是此变量对应的节点位置。

例如你想要插入到class为ai-content的元素内的开头，可以使用：.ai-content`,
          defaultValue: "",
          placeholder: "请输入CSS选择器，例如：.ai-content",
        },
        {
          key: "beginningText",
          label: "摘要开头文本",
          type: "input",
          tooltip:
            "当设置此变量后，摘要的开头将被更改为自定义的内容。\n\n例如：这篇文章介绍了",
          defaultValue: "",
          placeholder: "请输入自定义开头文本",
        },
        {
          key: "loadingText",
          label: "加载提示文本",
          type: "switch",
          tooltip: '控制是否在加载中时显示"加载中..."字样',
          defaultValue: true,
          span: 24,
        },
        {
          key: "theme",
          label: "摘要主题",
          type: "select",
          tooltip: `PostChat的文章摘要提供多种主题可供选择：

default: 默认主题
simple: 简约主题
yanzhi: 胭脂主题
menghuan: 梦幻主题`,
          defaultValue: "default",
          options: [
            { label: "默认主题", value: "default" },
            { label: "简约主题", value: "simple" },
            { label: "胭脂主题", value: "yanzhi" },
            { label: "梦幻主题", value: "menghuan" },
          ],
        },
        {
          key: "recommend",
          label: "推荐文章数量",
          type: "input",
          tooltip: `在文章底部显示推荐文章组件。数字代表数量，默认为0（关闭），最大为10。设为0则不显示推荐文章。`,
          defaultValue: 0,
          placeholder: "0-10，0表示不显示",
          span: 12,
          hidden: () => !chatAssistant,
        },
        {
          key: "tianliGPT_podcast",
          label: "AI播客功能",
          type: "switch",
          tooltip: `当启用后，将启用AI播客功能。默认关闭。`,
          defaultValue: false,
          span: 24,
          hidden: () => !chatAssistant,
        },
      ],
    },
  };

  // 添加 ConfigField 组件的 Props 类型定义
  interface ConfigFieldProps {
    item: ConfigItem;
    value: any;
    onChange: (value: any) => void;
  }

  // 将 ConfigField 组件移到组件内部
  const ConfigField: React.FC<ConfigFieldProps> = ({
    item,
    value,
    onChange,
  }) => {
    // 添加本地状态来存储输入值
    const [localValue, setLocalValue] = useState(value);

    // 当外部 value 改变时更新本地状态
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const renderField = () => {
      switch (item.type) {
        case "input":
          return (
            <Input
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)} // 只更新本地状态
              onBlur={() => onChange(localValue)} // 在失去焦点时才更新父组件状态
              placeholder={item.placeholder}
            />
          );
        case "color":
          return (
            <Input
              type="color"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={() => onChange(localValue)}
              style={{ width: "100%" }}
            />
          );
        case "checkbox":
          return (
            <Checkbox
              checked={value}
              onChange={(e) => onChange(e.target.checked)}
            >
              {item.label}
            </Checkbox>
          );
        case "select":
          return (
            <Select
              showSearch={!item.mode}
              mode={item.mode}
              style={{ width: "100%" }}
              value={value}
              onChange={onChange}
              options={item.options}
              placeholder={item.placeholder}
            />
          );
        case "switch":
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: "8px" }}>{item.label}</span>
              <Switch checked={value} onChange={onChange} />
              {item.tooltip && (
                <Tooltip title={item.tooltip}>
                  <InfoCircleOutlined
                    style={{
                      marginLeft: "4px",
                      color: "#1677ff",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              )}
            </div>
          );
        default:
          return null;
      }
    };

    if (item.type === "checkbox" || item.type === "switch") {
      return renderField();
    }

    return (
      <div>
        <div
          style={{
            marginBottom: "8px",
            color: "rgba(0, 0, 0, 0.88)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span>{item.label}</span>
          {item.tooltip && (
            <Tooltip title={item.tooltip}>
              <InfoCircleOutlined
                style={{
                  marginLeft: "4px",
                  color: "#1677ff",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}
        </div>
        {renderField()}
      </div>
    );
  };

  // 将 renderConfigGroup 移到组件内部
  const renderConfigGroup = (
    group: ConfigGroup,
    configParams: typeof defaultConfigParams,
    setConfigParams: React.Dispatch<
      React.SetStateAction<typeof defaultConfigParams>
    >
  ) => {
    return (
      <div style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 16 }}>
          {group.title}
        </Title>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {group.items.map((item) => {
            // 如果是 iframe 独占的配置项，且当前不是 iframe 模式，则不显示
            if (
              (item.key === "frameWidth" || item.key === "frameHeight") &&
              configParams.userMode !== "iframe"
            ) {
              return null;
            }

            // 如果是 magic 模式独占的配置项，且当前不是 magic 模式，则不显示
            if (item.key === "userIcon" && configParams.userMode !== "magic") {
              return null;
            }

            // 如果是 blackDom 配置项，检查显示条件
            if (item.key === "blackDom" && (!chatAssistant || articleSummary)) {
              return null;
            }

            return (
              <ConfigField
                key={item.key}
                item={item}
                value={configParams[item.key]}
                onChange={(value) =>
                  setConfigParams((prev) => ({
                    ...prev,
                    [item.key]: value,
                  }))
                }
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      {/* 顶部信息卡片 */}
      <Card
        variant="borderless"
        className="shadow-card"
        style={{
          marginBottom: 16,
          borderRadius: 8,
          background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
        }}
        styles={{
          body: { padding: "30px" },
        }}
      >
        <Row gutter={24} align="middle">
          <Col xs={24} md={16}>
            <Title
              level={2}
              style={{
                color: "white",
                margin: 0,
                marginBottom: 16,
              }}
            >
              代码生成器
            </Title>
            <Paragraph
              style={{
                color: "rgba(255, 255, 255, 0.85)",
                fontSize: 16,
                marginBottom: 0,
              }}
            >
              轻松生成适用于您网站的聊天助手和文章摘要嵌入代码，支持多种平台一键集成
            </Paragraph>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: "center" }}>
            <i
              className="ri-code-box-line"
              style={{ fontSize: 80, color: "rgba(255, 255, 255, 0.2)" }}
            />
          </Col>
        </Row>
      </Card>

      {/* 插件接入卡片 */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <i
              className="ri-plug-2-line"
              style={{
                fontSize: "20px",
                color: "#1890ff",
                marginRight: "8px",
              }}
            />
            <span>插件接入</span>
          </div>
        }
        variant="borderless"
        className="shadow-card"
        style={{ marginBottom: 16, borderRadius: 8 }}
        extra={
          <Tooltip title="查看更多插件详情">
            <Button
              type="link"
              icon={
                <i
                  className="ri-external-link-line"
                  style={{ fontSize: "16px" }}
                />
              }
              onClick={() =>
                window.open("https://postchat.zhheo.com/plugin/", "_blank")
              }
            >
              插件详情
            </Button>
          </Tooltip>
        }
      >
        <Paragraph style={{ marginBottom: "16px" }}>
          PostChat
          支持多种平台插件，您可以直接安装插件来快速配置，无需手动添加代码。
        </Paragraph>
        <Row gutter={[16, 16]}>
          {supportedSystems.map((system) => (
            <Col
              xs={12}
              sm={8}
              md={6}
              lg={4}
              key={`${system.name}-${system.icon}`}
            >
              <Card
                hoverable
                className="platform-card"
                style={{ textAlign: "center" }}
                onClick={() => window.open(system.url, "_blank")}
                styles={{
                  body: { padding: "16px 8px" },
                }}
              >
                <Space direction="vertical" size="small">
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      background: `${system.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px auto",
                    }}
                  >
                    <i
                      className={system.icon}
                      style={{ fontSize: "24px", color: system.color }}
                    />
                  </div>
                  <div style={{ fontWeight: 500 }}>{system.name}</div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* 代码生成区域 */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <i
              className="ri-code-box-line"
              style={{
                fontSize: "20px",
                color: "#1890ff",
                marginRight: "8px",
              }}
            />
            <span>代码生成器</span>
          </div>
        }
        variant="borderless"
        className="shadow-card"
        style={{ borderRadius: 8 }}
      >
        <div
          style={{
            padding: "12px 16px",
            background: "#FFF7E6",
            border: "1px solid #FFE58F",
            borderRadius: "8px",
            marginBottom: "24px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <i
            className="ri-error-warning-line"
            style={{ color: "#FA8C16", fontSize: "20px" }}
          />
          <div>
            <Text strong style={{ color: "#D46B08" }}>
              重要提示：
            </Text>
            <Text style={{ color: "#D46B08" }}>
              插件与手动插入代码不兼容，请选择一种方式接入
            </Text>
          </div>
        </div>

        <Row gutter={24}>
          <Col xs={24} md={10} style={{ marginBottom: 24 }}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div>
                <Title
                  level={5}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <span>项目配置</span>
                  <Tooltip title="选择您想要生成代码的项目">
                    <InfoCircleOutlined
                      style={{ marginLeft: 8, fontSize: 14, color: "#1677ff" }}
                    />
                  </Tooltip>
                </Title>
                <Space
                  direction="vertical"
                  style={{ width: "100%" }}
                  size="middle"
                >
                  <div>
                    <div style={{ marginBottom: "8px", color: "#666" }}>
                      当前项目
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <i
                        className={
                          routeProject?.is_postchat
                            ? "ri-message-3-line"
                            : "ri-file-list-line"
                        }
                        style={{
                          marginRight: 8,
                          color: routeProject?.is_postchat ? "#1890ff" : "#52c41a",
                        }}
                      />
                      <span style={{ fontWeight: 500 }}>{routeProject?.name || "—"}</span>
                      <Tag
                        color={routeProject?.is_postchat ? "blue" : "green"}
                        style={{ marginLeft: 8 }}
                      >
                        {routeProject?.is_postchat ? "全功能项目" : "仅摘要AI项目"}
                      </Tag>
                    </div>
                  </div>

                  {currentProjectType === "postchat" && chatAssistant && (
                    <div>
                      <div style={{ marginBottom: "8px", color: "#666" }}>
                        选择智能体
                      </div>
                      <Select
                        placeholder="选择默认或指定智能体"
                        style={{ width: "100%" }}
                        value={selectedAgentId}
                        loading={agentsLoading}
                        onChange={setSelectedAgentId}
                        disabled={!agents.length}
                        optionLabelProp="children"
                      >
                        {agents.map((agent) => (
                          <Option key={agent.id} value={agent.id}>
                            <Space>
                              <span>{agent.name}</span>
                              <Tag color={agent.status === "active" ? "success" : "default"}>
                                {agent.status === "active" ? "已发布" : "草稿"}
                              </Tag>
                              {agent.share_token && <Tag color="cyan">可嵌入</Tag>}
                            </Space>
                          </Option>
                        ))}
                      </Select>
                      {selectedAgent && !selectedAgent.share_token && (
                        <Alert
                          type="info"
                          showIcon
                          style={{ marginTop: 12 }}
                          message="当前智能体未开启分享，生成器会继续输出旧项目 Key 兼容代码。"
                        />
                      )}
                      {!agents.length && !agentsLoading && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginTop: 12 }}
                          message="当前项目还没有智能体，请先在 Agent Studio 中创建或确认默认 RAG Workflow。"
                        />
                      )}
                    </div>
                  )}

                  <Title level={5} style={{ marginBottom: "0px" }}>
                    功能选择
                  </Title>
                  <div
                    style={{
                      padding: "16px",
                      background: "#f0f7ff",
                      borderRadius: 8,
                      border: "1px solid #d6e8fc",
                    }}
                  >
                    <div style={{ marginBottom: 16 }}>
                      <Checkbox
                        checked={chatAssistant}
                        onChange={(e) => setChatAssistant(e.target.checked)}
                        disabled={currentProjectType === "summary"}
                      >
                        <Space>
                          <span
                            style={{
                              fontSize: 15,
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <i
                              className="ri-message-3-line"
                              style={{
                                marginRight: 8,
                                color: "#1890ff",
                                fontSize: 18,
                              }}
                            />
                            聊天助手
                          </span>
                          <Tooltip title="添加浮动聊天窗口到您的网站">
                            <InfoCircleOutlined style={{ color: "#1677ff" }} />
                          </Tooltip>
                        </Space>
                      </Checkbox>
                      {currentProjectType === "summary" &&
                        chatAssistant === false && (
                          <div
                            style={{
                              marginTop: 4,
                              color: "#ff4d4f",
                              fontSize: 12,
                            }}
                          >
                            仅摘要项目不支持聊天助手功能
                          </div>
                        )}
                    </div>
                    <div>
                      <Checkbox
                        checked={articleSummary}
                        onChange={(e) => setArticleSummary(e.target.checked)}
                      >
                        <Space>
                          <span
                            style={{
                              fontSize: 15,
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <i
                              className="ri-file-list-line"
                              style={{
                                marginRight: 8,
                                color: "#52c41a",
                                fontSize: 18,
                              }}
                            />
                            文章摘要
                          </span>
                          <Tooltip title="为您的文章自动生成AI摘要">
                            <InfoCircleOutlined style={{ color: "#1677ff" }} />
                          </Tooltip>
                        </Space>
                      </Checkbox>
                    </div>
                  </div>

                  <Title level={5} style={{ margin: "0px" }}>
                    功能配置
                  </Title>
                  <div
                    style={{
                      padding: "16px",
                      background: "#f0f7ff",
                      borderRadius: 8,
                      border: "1px solid #d6e8fc",
                    }}
                  >
                    <Space
                      direction="vertical"
                      style={{ width: "100%" }}
                      size="middle"
                    >
                      {chatAssistant && (
                        <>
                          {renderConfigGroup(
                            CONFIG_GROUPS.chat,
                            configParams,
                            setConfigParams
                          )}
                        </>
                      )}

                      {articleSummary && (
                        <>
                          {renderConfigGroup(
                            CONFIG_GROUPS.article,
                            configParams,
                            setConfigParams
                          )}
                        </>
                      )}
                    </Space>
                  </div>
                </Space>
              </div>
            </Space>
          </Col>

          <Col xs={24} md={14}>
            <Affix offsetTop={80}>
              <div
                style={{
                  maxHeight: "calc(100vh - 32px)",
                  overflow: "auto",
                  paddingRight: 8,
                }}
              >
                <Title
                  level={5}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <span>生成的代码</span>
                  <Tooltip title="将此代码添加到您的网站页面中">
                    <InfoCircleOutlined
                      style={{ marginLeft: 8, fontSize: 14, color: "#1677ff" }}
                    />
                  </Tooltip>
                </Title>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  <div
                    style={{
                      border: "1px solid #d9d9d9",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 16px",
                        background: "#fafafa",
                        borderBottom: "1px solid #d9d9d9",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Text strong>HTML</Text>
                      <Button
                        type={copied ? "primary" : "text"}
                        icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                        onClick={handleCopyCode}
                      >
                        {copied ? "已复制" : "复制代码"}
                      </Button>
                    </div>
                    <div
                      style={{
                        maxHeight: "350px",
                        overflow: "auto",
                        background: "#fff",
                      }}
                    >
                      <SyntaxHighlighter
                        language="javascript"
                        style={docco}
                        customStyle={{
                          margin: 0,
                          background: "#fff",
                          padding: "8px 16px",
                        }}
                      >
                        {code}
                      </SyntaxHighlighter>
                    </div>
                  </div>
                </div>

                {legacyCode && (
                  <Collapse
                    style={{ marginTop: 16 }}
                    items={[
                      {
                        key: "legacy",
                        label: "Compatibility mode: legacy snippet",
                        children: (
                          <div
                            style={{
                              border: "1px solid #d9d9d9",
                              borderRadius: 8,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                padding: "8px 16px",
                                background: "#fafafa",
                                borderBottom: "1px solid #d9d9d9",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Text strong>Legacy HTML</Text>
                              <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                  navigator.clipboard.writeText(legacyCode);
                                  message.success("Legacy code copied");
                                }}
                              >
                                Copy legacy code
                              </Button>
                            </div>
                            <div
                              style={{
                                maxHeight: "260px",
                                overflow: "auto",
                                background: "#fff",
                              }}
                            >
                              <SyntaxHighlighter
                                language="javascript"
                                style={docco}
                                customStyle={{
                                  margin: 0,
                                  background: "#fff",
                                  padding: "8px 16px",
                                }}
                              >
                                {legacyCode}
                              </SyntaxHighlighter>
                            </div>
                          </div>
                        ),
                      },
                    ]}
                  />
                )}

                <div
                  style={{
                    padding: "16px",
                    background: "#f0f7ff",
                    borderRadius: 8,
                    border: "1px solid #d6e8fc",
                    marginTop: "16px",
                  }}
                >
                  <Title
                    level={5}
                    style={{
                      color: "#1677ff",
                      marginTop: 0,
                      marginBottom: 8,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i
                      className="ri-lightbulb-line"
                      style={{ marginRight: 8 }}
                    />
                    接入帮助
                  </Title>
                  <Paragraph style={{ marginBottom: 4 }}>
                    1. 将上方生成的代码复制到您网站的HTML中，一般放在
                    <Text code>&lt;body&gt;</Text>标签结束前
                  </Paragraph>
                  <Paragraph style={{ marginBottom: 4 }}>
                    2. 如果您使用了文章摘要功能，需要设置
                    <Text code>tianliGPT_postSelector</Text>参数
                  ，具体配置方法请查看
                    <Button
                      type="link"
                      style={{ padding: "0 4px" }}
                      onClick={() =>
                        window.open(
                          "https://ai.zhheo.com/docs/summary.html",
                          "_blank"
                        )
                      }
                    >
                      如何获取
                    </Button>
                  </Paragraph>
                  <Paragraph style={{ marginBottom: 0 }}>
                    3. 更多配置教程请查看
                    <Button
                      type="link"
                      style={{ padding: "0 4px" }}
                      onClick={() =>
                        window.open(
                          "https://ai.zhheo.com/docs/",
                          "_blank"
                        )
                      }
                    >
                      详细文档
                    </Button>
                  </Paragraph>
                </div>
              </div>
            </Affix>
          </Col>
        </Row>
      </Card>

      <style>{`
        .shadow-card {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }

        .platform-card {
          transition: all 0.3s ease;
          height: 100%;
        }

        .platform-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
        }

        .no-highlight.ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
          background-color: transparent;
          font-weight: normal;
        }
        .no-highlight.ant-select-item-option-active:not(.ant-select-item-option-disabled) {
          background-color: rgba(0, 0, 0, 0.04);
        }
      `}</style>
    </div>
  );
};

export default CodeGenerator;
